import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import {
  Bell,
  Check,
  CheckCircle,
  Compass,
  Flame,
  Mic,
  Target,
  UserPlus,
  Users,
} from 'lucide-react'
import { type RefObject, useEffect, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { CoachNavButton } from '@/components/coach-nav-button'
import { CoachNavCallPill } from '@/components/coach-nav-call-pill'
import { useIsPro } from '@/hooks/use-is-pro'
import { setBottomNavMounted } from '@/lib/coach-call-store'
import { useIsCoaching } from '@/lib/coaching-mode'
import { scrollActiveTabToTop } from '@/lib/scroll'
import { openCapture, useCaptureStore } from '@/store/capture.store'
import { useUserMode } from '@/store/user-mode.store'

const HUMP = 'M0 28 C20 28 22 5 50 5 C78 5 80 28 100 28'

const NAV_ITEMS = [
  { to: '/today', icon: CheckCircle, labelKey: 'nav.today', exact: false },
  { to: '/goals', icon: Target, labelKey: 'nav.goals', exact: true },
  null,
  { to: '/everyday', icon: Flame, labelKey: 'nav.inbox', exact: false },
  { to: '/explore', icon: Compass, labelKey: 'nav.discover', exact: false },
] as const

// Coaching mode: clients, their shared plans, invite in the middle, the template library, and
// notifications (client replies and links land there).
const COACH_NAV_ITEMS = [
  { to: '/coaching', icon: Users, labelKey: 'coachingMode.clients', exact: false },
  { to: '/coaching/plans', icon: Target, labelKey: 'coachingMode.plans', exact: false },
  null,
  { to: '/coaching/library', icon: Compass, labelKey: 'coachingMode.library', exact: false },
  { to: '/notifications', icon: Bell, labelKey: 'nav.notifications', exact: false },
] as const

const TEXT_INPUT = /^(text|search|email|url|tel|password|number)$/

function isTyping(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  if (el.isContentEditable || el instanceof HTMLTextAreaElement) return true
  return el instanceof HTMLInputElement && TEXT_INPUT.test(el.type)
}

// While typing on a touch device, keep the focused field clear of both the
// keyboard and this bar: once the keyboard has settled, scroll its container just
// enough that the field sits above whichever comes first.
function useKeepFieldVisible(navRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!window.matchMedia('(pointer: coarse)').matches) return
    const vv = window.visualViewport
    let timer: ReturnType<typeof setTimeout> | undefined

    const reveal = () => {
      const el = document.activeElement
      if (!isTyping(el)) return
      // A composer box should come fully into view; any other field just itself.
      // Nothing moves unless the keyboard or this bar would actually cover it.
      const visibleTop = vv ? vv.offsetTop : 0
      let bottom = vv ? vv.offsetTop + vv.height : window.innerHeight
      const nav = navRef.current?.getBoundingClientRect()
      if (nav && nav.height > 0 && nav.top < bottom) bottom = nav.top
      const box0 = (el as HTMLElement).closest<HTMLElement>('[data-keep-centered]')
      const fits = box0 && box0.getBoundingClientRect().height < bottom - visibleTop - 24
      const rect = (fits ? box0 : (el as HTMLElement)).getBoundingClientRect()
      const overlap = rect.bottom + 12 - bottom
      if (overlap <= 0 && rect.top >= visibleTop) return
      let box = el!.parentElement
      while (box && box !== document.body) {
        const { overflowY } = getComputedStyle(box)
        if ((overflowY === 'auto' || overflowY === 'scroll') && box.scrollHeight > box.clientHeight)
          break
        box = box.parentElement
      }
      const delta = overlap > 0 ? overlap : rect.top - visibleTop - 12
      if (box && box !== document.body) box.scrollBy({ top: delta, behavior: 'instant' })
      else window.scrollBy({ top: delta, behavior: 'instant' })
    }
    // The keyboard animates in over ~300ms; measure after it lands.
    const schedule = () => {
      clearTimeout(timer)
      timer = setTimeout(reveal, 350)
    }
    document.addEventListener('focusin', schedule)
    vv?.addEventListener('resize', schedule)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('focusin', schedule)
      vv?.removeEventListener('resize', schedule)
    }
  }, [navRef])
}

export function BottomNav() {
  const { t } = useTranslation()
  const { location } = useRouterState()
  const { isPro, isLoading } = useIsPro()
  const currentPath = location.pathname
  const captureOpen = useCaptureStore((s) => s.open)
  const mic = useCaptureStore((s) => s.mic)
  const listening = captureOpen && mic === 'listening'
  const navRef = useRef<HTMLElement>(null)
  useKeepFieldVisible(navRef)
  const coaching = useIsCoaching()
  const navigate = useNavigate()
  const unread = useQuery(api.notifications.unreadCount, coaching ? {} : 'skip') ?? 0

  const items = coaching ? COACH_NAV_ITEMS : NAV_ITEMS
  const matches = (to: string, exact: boolean) =>
    currentPath === to || (!exact && currentPath.startsWith(to + '/'))

  // Onboarding is a dedicated full-screen flow; /goals/new mounts this only outside onboarding.
  const onboarding = currentPath.startsWith('/onboarding')
  useLayoutEffect(() => {
    if (onboarding) return
    setBottomNavMounted(true)
    return () => setBottomNavMounted(false)
  }, [onboarding])
  if (onboarding) return null

  return (
    <nav
      ref={navRef}
      className={cn(
        // Solid, like the hump: a translucent bar and hump never matched, and the capture sheet
        // grows out of it as one surface. Above the sheet's backdrop while it's open.
        'fixed inset-x-0 bottom-0 select-none bg-background [-webkit-touch-callout:none] lg:hidden',
        captureOpen ? 'z-[60]' : 'z-50',
      )}
    >
      <CoachNavCallPill />
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-full">
        {!captureOpen && (
          <>
            <span className="absolute -bottom-px left-0 h-px w-[calc(50%-50px)] bg-border" />
            <span className="absolute -bottom-px right-0 h-px w-[calc(50%-50px)] bg-border" />
          </>
        )}
        <div
          className="absolute -bottom-px left-1/2 h-7 w-[100px] -translate-x-1/2 bg-background"
          style={{ clipPath: `path('${HUMP} Z')` }}
        />
        <svg
          viewBox="0 0 100 28"
          className="absolute bottom-0 left-1/2 h-7 w-[100px] -translate-x-1/2 overflow-visible"
        >
          <path
            d={HUMP}
            transform="translate(0 0.5)"
            className={cn('fill-none', captureOpen ? 'stroke-transparent' : 'stroke-border')}
          />
        </svg>
      </div>
      <div
        className="flex items-stretch"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {items.map((item) => {
          if (!item && coaching) {
            return (
              <div key="invite" className="relative flex flex-1">
                <button
                  type="button"
                  aria-label={t('coachingMode.inviteClient')}
                  onClick={() => {
                    useUserMode.getState().requestInvite(true)
                    void navigate({ to: '/coaching' })
                  }}
                  className="group flex flex-1 cursor-pointer flex-col items-center gap-1 whitespace-nowrap py-2.5 text-[10px] font-medium text-muted-foreground"
                >
                  <span className="relative h-5 w-12">
                    <span className="absolute bottom-0 left-0 flex h-12 w-12 items-center justify-center rounded-full bg-coach shadow-md shadow-black/30 transition-transform group-active:scale-95">
                      <UserPlus className="h-[22px] w-[22px] text-white" strokeWidth={2.25} />
                    </span>
                  </span>
                  {t('coachingMode.invite')}
                </button>
              </div>
            )
          }
          if (!item) {
            // The coach, except while Brain dump is docked above the bar: its mic then records / stops.
            return (
              <div key="capture" className="relative flex flex-1">
                {!captureOpen ? (
                  <CoachNavButton />
                ) : (
                  <button
                    type="button"
                    data-capture-toggle
                    aria-pressed={captureOpen}
                    aria-label={listening ? t('voice.done') : t('voice.title')}
                    onClick={() => {
                      const { open, micAction, setOpen } = useCaptureStore.getState()
                      // Open: the mic records / stops / resumes; otherwise (reviewing) it closes.
                      if (!open) openCapture()
                      else if (micAction) micAction()
                      else setOpen(false)
                    }}
                    className={cn(
                      'group flex flex-1 cursor-pointer flex-col items-center gap-1 whitespace-nowrap py-2.5 text-[10px] font-medium transition-colors',
                      listening
                        ? 'text-destructive'
                        : captureOpen
                          ? 'text-primary-text'
                          : 'text-muted-foreground',
                    )}
                  >
                    <span className="relative h-5 w-12">
                      <span
                        className={cn(
                          'absolute bottom-0 left-0 flex h-12 w-12 items-center justify-center rounded-full shadow-md shadow-black/30 transition-[transform,background-color] group-active:scale-95',
                          listening ? 'bg-destructive' : 'bg-muted',
                        )}
                      >
                        {listening ? (
                          <>
                            <span className="absolute inset-0 animate-ping rounded-full bg-destructive opacity-20 motion-reduce:animate-none" />
                            <Check
                              className="relative h-[22px] w-[22px] text-white"
                              strokeWidth={3}
                            />
                          </>
                        ) : (
                          <Mic
                            className={cn(
                              'h-[22px] w-[22px] transition-colors',
                              captureOpen ? 'text-primary-text' : 'text-foreground',
                            )}
                            strokeWidth={2.25}
                          />
                        )}
                        {!isPro && !isLoading && (
                          <span className="absolute -top-1 -right-1.5 rounded-full border border-background bg-foreground px-1.5 py-px text-[9px] font-bold uppercase leading-tight text-background">
                            {t('settings.planPro')}
                          </span>
                        )}
                      </span>
                    </span>
                    {listening ? t('voice.done') : t('voice.title')}
                  </button>
                )}
              </div>
            )
          }
          const { to, icon: Icon, labelKey, exact } = item
          // While Brain dump is open it's the active tab.
          const isActive =
            !captureOpen &&
            !currentPath.startsWith('/goals/new') &&
            matches(to, exact) &&
            !items.some((o) => o && o.to.length > to.length && matches(o.to, o.exact))
          return (
            <Link
              key={to}
              to={to}
              onClick={() => {
                // Tapping the tab you're already on scrolls it back to top.
                if (currentPath === to) scrollActiveTabToTop()
              }}
              className={cn(
                'flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition-colors',
                isActive ? 'text-primary-text' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <span className="relative">
                <Icon className={cn('h-5 w-5', isActive ? 'stroke-[2.5]' : 'stroke-[1.5]')} />
                {to === '/notifications' && unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-primary ring-2 ring-background" />
                )}
              </span>
              {t(labelKey)}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
